// The console's own sign-in page (/login). Nothing happens until the admin clicks the button: only then does the
// browser go to the auth server (OIDC authorization code + PKCE) and come back to `returnTo`.
// Layout: illustration panel (navy, like the sidebar) + the sign-in column. Illustration: unDraw "Authentication"
// (unDraw license — THIRD_PARTY_NOTICES.md), recoloured to the brand blue.
import { useState } from "react";
import { LogIn } from "lucide-react";
import { Alert, Button } from "@platform/ui";
import illustration from "../assets/illustrations/authentication.svg";
import type { ConsoleAuth } from "../auth";
import { setLocale, useT } from "../i18n";

export function LoginPage({
  auth,
  returnTo,
  reason,
}: {
  auth: Pick<ConsoleAuth, "signIn">;
  returnTo: string;
  reason: "expired" | "signedOut" | null;
}) {
  const { t, locale } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const productName = t("app.name");

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await auth.signIn(returnTo);
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const brand = (
    <div className="flex items-center gap-3">
      <span
        aria-hidden
        className="flex size-10 items-center justify-center rounded-xl bg-primary text-lg font-semibold text-white"
      >
        {productName.charAt(0).toUpperCase()}
      </span>
      <div>
        <div className="font-semibold">{productName}</div>
        <div className="text-sm opacity-70">{t("app.console")}</div>
      </div>
    </div>
  );

  return (
    <main className="grid min-h-screen bg-paper md:grid-cols-2 lg:grid-cols-5">
      <section
        aria-label={productName}
        className="order-1 flex flex-col gap-8 bg-nav p-8 text-white md:-order-1 md:rounded-r-2xl lg:col-span-3 lg:p-12"
      >
        <div className="hidden md:block">{brand}</div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl sm:p-10">
            <img src={illustration} alt="" className="mx-auto max-h-80 w-full object-contain" />
          </div>
        </div>
        <div className="max-w-xl">
          <p className="text-2xl leading-snug font-semibold">{t("login.heroTitle")}</p>
          <p className="mt-2 text-sm leading-relaxed text-nav-text">{t("login.heroBody")}</p>
        </div>
      </section>

      <div className="flex items-center justify-center p-6 md:p-10 lg:col-span-2">
        <div className="w-full max-w-md">
          <div className="mb-10 text-ink md:hidden">{brand}</div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">{t("login.title")}</h1>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">{t("login.subtitle")}</p>
          <div className="mt-8 space-y-4">
            {reason && (
              <Alert tone={reason === "expired" ? "warning" : "info"}>
                {t(reason === "expired" ? "login.expired" : "login.signedOut")}
              </Alert>
            )}
            {error && (
              <Alert tone="error">
                {t("login.unavailable")} <span className="break-words text-ink-2">({error})</span>
              </Alert>
            )}
            <Button block size="lg" loading={busy} onClick={signIn} data-testid="console-sign-in">
              <LogIn aria-hidden className="size-4" />
              {busy ? t("auth.redirecting") : t("login.button", { name: productName })}
            </Button>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-ink-2">{t("login.hint")}</p>
          <div className="mt-10 flex gap-3 text-sm text-ink-2" aria-label={t("header.language")}>
            {(["th", "en"] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLocale(l)}
                aria-pressed={locale === l}
                className={locale === l ? "font-medium text-ink" : "hover:text-ink"}
              >
                {l === "th" ? "ไทย" : "English"}
              </button>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
